import React from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  HandsPraying,
  UsersThree,
  BookOpen,
  Quotes,
  ArrowRight,
  HandHeart,
  Sun,
  Leaf,
  ChatTeardropDots,
} from "@phosphor-icons/react";
import { IMAGES, SITE } from "../lib/site";
import SectionHeading from "../components/SectionHeading";
import OnlineChapelSection from "../components/OnlineChapelSection";
import Seo from "../components/Seo";

const supportAreas = [
  {
    icon: HandsPraying,
    title: "Grief & Bereavement Support",
    text: "Compassionate walking-alongside care for the loss of a loved one — at any stage of grief.",
  },
  {
    icon: Heart,
    title: "Emotional Support",
    text: "Gentle, faith-rooted encouragement for anxiety, sorrow, loneliness, and discouragement.",
  },
  {
    icon: UsersThree,
    title: "Caregiver Encouragement",
    text: "Refreshment and practical tools for those caring for aging parents, ill spouses, or hurting loved ones.",
  },
  {
    icon: Sun,
    title: "Spiritual Care",
    text: "Chaplain-led prayer, Scripture, and reflection for seasons of doubt, weariness, or transition.",
  },
  {
    icon: Leaf,
    title: "Life Restoration",
    text: "Hope and practical next steps for rebuilding after divorce, illness, addiction, or major loss.",
  },
  {
    icon: ChatTeardropDots,
    title: "Prayer Support",
    text: "Submit a prayer request privately and join a community holding you up in prayer.",
  },
];

const testimonials = [
  {
    quote:
      "After my husband passed, I felt like I was disappearing. The encouragement and prayer support here gently helped me find my footing again.",
    name: "Margaret",
    role: "Bereaved spouse",
  },
  {
    quote:
      "As a full-time caregiver for my mother, I was running on empty. The caregiver resources reminded me that I matter, too — and that God sees me.",
    name: "Daniel",
    role: "Family caregiver",
  },
  {
    quote:
      "The chaplain-led workshop on grief was honest, gentle, and faith-filled. I left feeling held instead of fixed.",
    name: "Rosa",
    role: "Workshop participant",
  },
];

const Home = () => {
  return (
    <div data-testid="page-home">
      <Seo
        title="Faith-Based Grief Support & Spiritual Care"
        description="Begin Again Living Ministries offers faith-based grief support, spiritual care, caregiver support, and an Online Chapel by Zoom for those walking through loss."
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 items-center pt-12 sm:pt-20 pb-20 sm:pb-28">
          <div className="lg:col-span-6 reveal">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              A faith-based ministry of hope
            </span>
            <h1 className="mt-6 font-heading font-medium text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] text-ink-900">
              Helping People{" "}
              <span className="italic text-primary">Begin Again</span> After
              Loss, Grief, and Life's Hardest Seasons
            </h1>
            <p className="mt-7 text-ink-700 text-base sm:text-lg leading-relaxed max-w-xl">
              {SITE.name} provides faith-based encouragement, grief support,
              spiritual care resources, and practical tools for individuals,
              families, caregivers, and communities seeking hope after
              difficult seasons.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/request-support"
                data-testid="hero-request-support-btn"
                className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                Request Support
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/donate"
                data-testid="hero-partner-btn"
                className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-7 py-3.5 font-semibold hover:bg-primary/5 transition-all duration-300"
              >
                Partner With the Ministry
                <HandHeart size={18} weight="duotone" />
              </Link>
            </div>

            <div className="mt-12 flex items-center gap-6 text-sm text-ink-500">
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-secondary" />
                Confidential prayer support
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-primary" />
                Chaplain-led care
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 reveal">
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-40 h-40 rounded-full bg-secondary/10 blur-2xl" />
              <div className="absolute -bottom-10 -right-10 w-56 h-56 rounded-full bg-primary/10 blur-3xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-ink-100 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.25)]">
                <img
                  src={IMAGES.hero}
                  alt="Peaceful sunrise representing hope and renewal"
                  className="w-full h-[420px] sm:h-[520px] object-cover"
                />
              </div>

              <div className="hidden sm:flex absolute -bottom-8 left-8 bg-white rounded-2xl shadow-[0_20px_50px_-15px_rgba(15,23,42,0.2)] border border-ink-100 p-5 max-w-xs items-start gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary shrink-0">
                  <Quotes size={20} weight="duotone" />
                </span>
                <p className="text-sm text-ink-700 leading-relaxed italic">
                  "He heals the brokenhearted and binds up their wounds."
                  <span className="block not-italic text-xs text-ink-500 mt-2">
                    Psalm 147:3
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MINISTRY OVERVIEW */}
      <section className="bg-paper border-y border-ink-100 py-20 sm:py-28">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <SectionHeading
                overline="Our ministry"
                title="A gentle place to breathe, grieve, and begin again."
              />
            </div>
            <div className="lg:col-span-7">
              <p className="text-ink-700 text-lg leading-relaxed">
                We exist for the person sitting in the quiet ache after a
                funeral, the caregiver who hasn't slept in months, and the soul
                wondering if hope is still possible. Through chaplain-led
                encouragement, prayer, and faith-rooted resources, we walk
                slowly — and tenderly — with people in their hardest seasons.
              </p>
              <div className="mt-10 grid sm:grid-cols-3 gap-6">
                {[
                  { k: "Faith-rooted", v: "Christ-centered care, never pushy." },
                  { k: "Chaplain-led", v: "Trained spiritual care at the heart." },
                  { k: "Whole-person", v: "Body, mind, spirit, and community." },
                ].map((b) => (
                  <div key={b.k} className="border-l-2 border-secondary pl-4">
                    <div className="text-primary font-semibold text-sm uppercase tracking-wider">
                      {b.k}
                    </div>
                    <div className="mt-2 text-ink-700 text-sm leading-relaxed">{b.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AREAS OF SUPPORT */}
      <section className="py-20 sm:py-28">
        <div className="container-x">
          <SectionHeading
            overline="Areas of support"
            title="Care for the moments that change everything."
            subtitle="Whether you are grieving, caregiving, or quietly rebuilding, there is a place for you here."
            align="center"
            className="mb-16"
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {supportAreas.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                data-testid={`support-area-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group bg-white rounded-2xl p-8 border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_18px_50px_-10px_rgba(15,23,42,0.15)] transition-all duration-300"
              >
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-softblue text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon size={24} weight="duotone" />
                </span>
                <h3 className="mt-6 font-heading text-2xl text-ink-900 font-medium">
                  {title}
                </h3>
                <p className="mt-3 text-ink-700 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/grief-support"
              data-testid="explore-support-btn"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
            >
              Explore all areas of support
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED RESOURCES */}
      <section className="py-20 sm:py-28 bg-softblue/50">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-6 bg-secondary/10 rounded-[2rem] -rotate-2" />
                <img
                  src={IMAGES.journal}
                  alt="Open journal and Bible on a wooden table"
                  className="relative rounded-2xl shadow-2xl border border-white"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <SectionHeading
                overline="Books & Publications"
                title="Resources to hold in your hands when you need guidance for the journey."
                subtitle="Explore books, guided workbooks, professional resources, and faith-centered publications created to support individuals, families, caregivers, chaplains, and compassionate leaders."
              />
              <Link
                to="/books"
                data-testid="explore-resources-btn"
                className="mt-8 inline-flex items-center gap-2 bg-primary text-white rounded-full px-6 py-3 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                <BookOpen size={18} weight="duotone" />
                Explore Books & Publications
              </Link>
              <div className="mt-5 text-sm text-ink-600">
                Or{" "}
                <Link
                  to="/sponsor-a-book"
                  data-testid="books-sponsor-a-book-link"
                  className="inline-flex items-center gap-1 text-primary font-semibold hover:gap-2 transition-all"
                >
                  <HandHeart size={16} weight="duotone" />
                  sponsor a book for someone in grief
                  <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 sm:py-28">
        <div className="container-x">
          <SectionHeading
            overline="Stories of hope"
            title="Real people. Real grief. Real grace."
            align="center"
            className="mb-16"
          />
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <figure
                key={t.name}
                className="bg-white rounded-2xl p-8 border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
                data-testid={`testimonial-${t.name.toLowerCase()}`}
              >
                <Quotes size={28} weight="duotone" className="text-secondary" />
                <blockquote className="mt-5 font-heading italic text-xl text-ink-800 leading-snug">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-ink-100">
                  <div className="font-semibold text-ink-900">{t.name}</div>
                  <div className="text-sm text-ink-500">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ONLINE SUPPORT INFO */}
      <section className="py-20 sm:py-28 bg-paper border-y border-ink-100">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img
              src={IMAGES.counseling}
              alt="Compassionate listening session"
              className="rounded-2xl shadow-xl border border-ink-100"
            />
          </div>
          <div className="lg:col-span-6">
            <SectionHeading
              overline="Online ministry"
              title="Come as you are — from wherever you are."
              subtitle="Join chaplain-led grief workshops, encouragement classes, caregiver webinars, and gentle self-care sessions online — at your own pace, on your own screen."
            />
            <Link
              to="/classes"
              data-testid="see-classes-btn"
              className="mt-8 inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-6 py-3 font-semibold hover:bg-primary/5 transition-all duration-300"
            >
              See upcoming classes
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* ONLINE CHAPEL */}
      <OnlineChapelSection variant="home" />

      {/* DONATION CTA */}
      <section className="py-20 sm:py-28">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-primary-dark p-10 sm:p-16 text-white">
            <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-secondary/30 blur-3xl" />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <span className="text-xs uppercase tracking-[0.22em] text-secondary font-semibold">
                  Partner with us
                </span>
                <h2 className="mt-5 font-heading text-3xl sm:text-5xl font-medium leading-[1.1]">
                  Your generosity carries hope to someone in their hardest hour.
                </h2>
                <p className="mt-5 text-white/85 max-w-2xl leading-relaxed">
                  Every gift directly supports grief outreach, free workshops,
                  printed resources, and chaplain-led encouragement for people
                  who can't afford care during their most fragile season.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link
                  to="/donate"
                  data-testid="cta-donate-btn"
                  className="inline-flex items-center gap-2 bg-secondary text-white rounded-full px-7 py-4 font-semibold hover:bg-secondary-dark hover:-translate-y-0.5 hover:shadow-xl transition-all duration-300"
                >
                  <HandHeart size={20} weight="fill" />
                  Give a Gift of Hope
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRAYER REQUEST PROMPT */}
      <section className="py-20 sm:py-28 bg-softblue/40">
        <div className="container-x">
          <div className="max-w-3xl mx-auto text-center">
            <HandsPraying size={42} weight="duotone" className="text-primary mx-auto" />
            <h2 className="mt-6 font-heading text-3xl sm:text-5xl font-medium text-ink-900 leading-[1.1]">
              Carry a quiet burden today?
            </h2>
            <p className="mt-5 text-ink-700 text-lg leading-relaxed">
              You don't have to find the right words. Share what you can —
              we'll pray, and we'll be here.
            </p>
            <Link
              to="/request-support"
              data-testid="prayer-cta-btn"
              className="mt-8 inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              <HandsPraying size={18} weight="duotone" />
              Submit a Prayer Request
            </Link>
          </div>
        </div>
      </section>

      {/* ENCOURAGEMENT BANNER */}
      <section className="py-16 bg-white">
        <div className="container-x">
          <div className="border-y border-ink-100 py-12 text-center">
            <p className="font-heading italic text-2xl sm:text-3xl text-ink-800 leading-relaxed max-w-3xl mx-auto">
              "Blessed are those who mourn, for they shall be comforted."
            </p>
            <p className="mt-4 text-sm text-primary tracking-[0.22em] uppercase font-semibold">
              Matthew 5:4
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
